import axios from 'axios'

axios.defaults.headers.common['Accept'] = 'application/json'

export {
  // Authentication
  createMagic, login, refresh,

  // Enrollments (LTI 1.1)
  getEnrollment, getEnrollments,
  processEnrollment, unProcessEnrollment,

  // Memberships (LTI 1.3)
  getMembership, getMemberships,
  processMembership, unProcessMembership,

  // Coupons
  checkCoupon, importCoupons,

  // Consumers (LTI 1.1)
  findConsumer, checkConsumerKey, createConsumer, updateConsumer,

  // Platforms (LTI 1.3)
  listPlatforms, createPlatform,
  updateCredential, createCredential,

  // Courses
  getTWACourse, createTWACourse, updateTWACourse,
  checkLtiCourse, checkLtiCourses,
  activateCourse, activateLtiCourse, reassignCourse,

  // Products
  getProducts, checkProduct, createProduct, updateProduct,

  // Domains
  checkDomain, addDomain, removeDomain,

  // Users
  getUsers,

  // Utilities
  checkCookie, searchTerms
}

const API_URL = process.env.VUE_APP_API_URL
const WP_URL = 'https://student.truewayasl.com/wp-json/wp/v2'

async function checkCookie () {
  const url = `${API_URL}/api/lti/ping`
  const response = await axios.get(url, {withCredentials: true})
  return response.data
}

function getAccessToken () {
  return localStorage.getItem('accessToken')
}

async function searchTerms (term, page) {
  let url = `${WP_URL}/encyclopedia?search=${term}&orderby=title&order=asc&per_page=100&page=${page}`
  let response = await axios.get(url)
  response.pages = parseInt(response.headers['x-wp-totalpages'])
  response.total = parseInt(response.headers['x-wp-total'])
  return response
}

async function createMagic (connection, target, recaptcha_response) {
  const url = `${API_URL}/api/auth/magic`
  const response = await axios.post(url, { connection: connection, target: target, recaptcha_response: recaptcha_response })
  return response.data
}

async function login (connection, target, code) {
  const url = `${API_URL}/api/auth/login`
  const response = await axios.post(url, { connection: connection, target: target, code: code })
  return response.data
}

async function refresh (token) {
  const url = `${API_URL}/api/auth/refresh`
  const response = await axios.post(url, { refresh_token: token })
  return response.data
}

async function getProducts () {
  const url = `${API_URL}/api/products`
  const response = await axios.get(url, { headers: { Authorization: `Bearer ${getAccessToken()}` } })
  return response.data.products || response.data
}

async function getTWACourse (id) {
  const url = `${API_URL}/api/courses/` + id
  const response = await axios.get(url, { headers: { Authorization: `Bearer ${getAccessToken()}` } })
  if (response.data) {
    return response.data.course
  } else {
    return response.data
  }
}

async function createTWACourse (course) {
  const url = `${API_URL}/api/courses`
  const response = await axios.post(url, { course }, { headers: { Authorization: `Bearer ${getAccessToken()}` } })
  return response.data.course
}

async function updateTWACourse (course) {
  const url = `${API_URL}/api/courses/` + course.canvas_course_id
  const response = await axios.put(url, { course }, { headers: { Authorization: `Bearer ${getAccessToken()}` } })
  return response.data.course
}

async function checkLtiCourse (context_id) {
  const url = `${API_URL}/twa/get_lti_course/` + context_id
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  })
  return response.data.lti_course
}

async function checkLtiCourses (context_id) {
  const url = `${API_URL}/twa/get_lti_courses/` + context_id
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  })
  return response.data.lti_courses
}

async function activateLtiCourse (lti_course) {
  const url = `${API_URL}/twa/activate_lti_course/` + lti_course.context_id
  const response = await axios.post(url, { lti_course }, { headers: { Authorization: `Bearer ${getAccessToken()}` } })
  return response.data.lti_course
}

// Move a course to another school (TWA-215). With dry_run the server writes nothing and
// returns only the impact summary, which populates the confirm dialog. Identified by
// row id, not context_id — on a cross-consumer collision the context_id is exactly what
// the two rows share.
//
// A real move returns { lti_course, impact, duplicate_id }, or { moved: true, message,
// impact, duplicate_id } when the move landed but the row could not be serialized.
// duplicate_id is non-null when a launch raced the move into creating a duplicate row
// under the target (TWA-829) — the move still stands; the two rows need merging.
async function reassignCourse (lti_course_id, target_key, dry_run = false) {
  const url = `${API_URL}/twa/reassign_course`
  const response = await axios.post(url, { lti_course_id, target_key, dry_run },
    { headers: { Authorization: `Bearer ${getAccessToken()}` } })
  return response.data
}

async function checkCoupon (code) {
  const url = `${API_URL}/api/coupons/` + code
  const response = await axios.get(url, { headers: { Authorization: `Bearer ${getAccessToken()}` } })
  return response.data.coupons
}

async function importCoupons(csvfile) {
  const url = `${API_URL}/twa/import_coupons`
  let formData = new FormData()
  formData.append('csvfile', csvfile);
  const response = await axios.post(url, formData, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data
}

async function getEnrollments(email) {
  const url = `${API_URL}/twa/get_enrollments`
  const response = await axios.post(url, {email: email}, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data.lti_enrollments
}

async function getEnrollment(token) {
  const url = `${API_URL}/twa/get_enrollment`
  const response = await axios.post(url, {token: token}, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data.lti_enrollment
}

async function processEnrollment(token) {
  const url = `${API_URL}/twa/process_enrollment`
  const response = await axios.post(url, {token: token}, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data  // Returns { lti_enrollment, action }
}

async function unProcessEnrollment(token) {
  const url = `${API_URL}/twa/unprocess_enrollment`
  const response = await axios.post(url, {token: token}, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data.lti_enrollment
}

async function getMemberships(email) {
  const url = `${API_URL}/twa/get_memberships`
  const response = await axios.post(url, {email: email}, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data.memberships
}

async function getMembership(token) {
  const url = `${API_URL}/twa/get_membership`
  const response = await axios.post(url, {token: token}, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data.membership
}

async function processMembership(token) {
  const url = `${API_URL}/twa/process_membership`
  const response = await axios.post(url, {token: token}, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data.membership
}

async function unProcessMembership(token) {
  const url = `${API_URL}/twa/unprocess_membership`
  const response = await axios.post(url, {token: token}, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data.membership
}

async function findConsumer(key) {
  const url = `${API_URL}/twa/find_consumer`
  const response = await axios.post(url, {key: key}, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data
}

async function checkConsumerKey(key, exclude_key) {
  const url = `${API_URL}/twa/check_consumer_key`
  const response = await axios.post(url, {key: key, exclude_key: exclude_key}, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data
}

async function createConsumer(key, name) {
  const url = `${API_URL}/twa/create_consumer`
  const response = await axios.post(url, {key: key, name: name}, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data
}

async function updateConsumer(key, accepts_payment, accepts_access_code, email_domain, product_id, new_key, name, shows_teacher_resources, auto_enroll_coupon_code, allows_wts, allow_mismatched_courses) {
  const url = `${API_URL}/twa/update_consumer`
  const body = {
    key: key,
    accepts_payment: accepts_payment,
    accepts_access_code: accepts_access_code,
    email_domain: email_domain,
    product_id: product_id,
    new_key: new_key,
    name: name
  }
  if (shows_teacher_resources !== undefined) body.shows_teacher_resources = shows_teacher_resources
  if (auto_enroll_coupon_code !== undefined) body.auto_enroll_coupon_code = auto_enroll_coupon_code
  if (allows_wts !== undefined) body.allows_wts = allows_wts
  // TWA-944: the admin confirmed that current courses on another product use their own access codes.
  if (allow_mismatched_courses) body.allow_mismatched_courses = true
  const response = await axios.post(url, body, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data
}

async function createPlatform(domain, d2l) {
  const url = `${API_URL}/twa/create_platform`
  const body = {domain, d2l}
  const response = await axios.post(url, body, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data
}

async function listPlatforms(query = null) {
  let url = `${API_URL}/twa/list_platforms`
  if (query) {
    url += `?query=${encodeURIComponent(query)}`
  }
  const response = await axios.get(url, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data
}

async function updateCredential(credential_id, school_name, accepts_payment, accepts_access_code, product_id, shows_teacher_resources, auto_enroll_coupon_code, allows_wts, allow_mismatched_courses) {
  const url = `${API_URL}/twa/update_credential`
  const body = {credential_id}
  if (school_name !== undefined) body.school_name = school_name
  if (accepts_payment !== undefined) body.accepts_payment = accepts_payment
  if (accepts_access_code !== undefined) body.accepts_access_code = accepts_access_code
  if (shows_teacher_resources !== undefined) body.shows_teacher_resources = shows_teacher_resources
  if (allows_wts !== undefined) body.allows_wts = allows_wts
  if (product_id !== undefined) body.product_id = product_id
  if (auto_enroll_coupon_code !== undefined) body.auto_enroll_coupon_code = auto_enroll_coupon_code
  // TWA-944: see updateConsumer.
  if (allow_mismatched_courses) body.allow_mismatched_courses = true
  const response = await axios.post(url, body, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data
}

async function createCredential(platform_id, client_id, accepts_payment, accepts_access_code, school_name) {
  const url = `${API_URL}/twa/create_credential`
  const body = {platform_id, client_id, accepts_payment, accepts_access_code}
  if (school_name) body.school_name = school_name
  const response = await axios.post(url, body, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data
}

async function activateCourse(token, product_code, create_coupon, shows_teacher_resources, auto_enroll_coupon_code, allows_wts) {
  const url = `${API_URL}/twa/activate_course`
  const body = {token: token, product_code: product_code, create_coupon: create_coupon}
  if (shows_teacher_resources !== undefined) body.shows_teacher_resources = shows_teacher_resources
  if (allows_wts !== undefined) body.allows_wts = allows_wts
  if (auto_enroll_coupon_code !== undefined) body.auto_enroll_coupon_code = auto_enroll_coupon_code
  const response = await axios.post(url, body, {headers: {Authorization: `Bearer ${getAccessToken()}`}})
  return response.data
}

async function getUsers () {
  const url = `${API_URL}/api/users`
  const response = await axios.get(url, { headers: { Authorization: `Bearer ${getAccessToken()}` } })
  return response.data.users || response.data
}

async function checkDomain(domain) {
  const url = `${API_URL}/twa/check_domain`
  const response = await axios.post(url, {domain: domain},{ headers: { Authorization: `Bearer ${getAccessToken()}` } })
  return response.data.accounts
}

async function addDomain(domain, units) {
  const url = `${API_URL}/twa/add_domain`
  const response = await axios.post(url, {domain: domain, units: units},{ headers: { Authorization: `Bearer ${getAccessToken()}` } })
  return response.data
}

async function removeDomain(domain, units) {
  const url = `${API_URL}/twa/remove_domain`
  const response = await axios.post(url, {domain: domain, units: units},{ headers: { Authorization: `Bearer ${getAccessToken()}` } })
  return response.data
}

async function checkProduct(product_code) {
  const url = `${API_URL}/twa/check_product`
  const response = await axios.post(url, {product_code: product_code},{ headers: { Authorization: `Bearer ${getAccessToken()}` } })
  return response.data
}

async function createProduct(product_code, stripe_price_id, name, units, count_unique_emails) {
  const url = `${API_URL}/twa/create_product`
  const response = await axios.post(url, {product_code: product_code, stripe_price_id: stripe_price_id, name: name, units: units, count_unique_emails: count_unique_emails},{ headers: { Authorization: `Bearer ${getAccessToken()}` } })
  return response.data
}

async function updateProduct(product_code, stripe_price_id, name, units, count_unique_emails) {
  const url = `${API_URL}/twa/update_product`
  const response = await axios.post(url, {product_code: product_code, stripe_price_id: stripe_price_id, name: name, units: units, count_unique_emails: count_unique_emails},{ headers: { Authorization: `Bearer ${getAccessToken()}` } })
  return response.data
}
